// Implements: F-UIX-020, F-UIX-007, F-UIX-008, F-UIX-009, F-DSG-006
/**
 * The logo and the reference images of a profile. Pure presentation, in two parts the editor places
 * apart (owner round 4, the mockup's compact card): the logo as ONE drop zone with a small thumbnail,
 * and the four reference images as a grid of small thumbnail tiles behind a disclosure.
 *
 * One slot per closed key (`logo`, `image-1`..`image-4`), so the five-image cap of a version is the
 * shape of the screen, not a check. A slot shows the stored image (read from the server as an `<img>`,
 * never a local preview; mockup v3 P5 lets the logo be an SVG the server cleaned and serves sandboxed)
 * and a file picker. The logo's zone also takes a dropped file, and highlights while one is over it. Only once a
 * file is chosen does the slot ask for its required alt text and offer its upload; Enter in the alt
 * text uploads, and never submits the profile form around it. A refusal, the browser's or the
 * server's, is shown inside the slot it belongs to; the person's alt text stays as typed.
 */

import { useState } from "react";
import type { DragEvent, ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { Field, describedBy, errorId } from "../../../shared/ui/molecules/Field";
import type { AssetKey } from "../model/referenceRules";
import styles from "./ReferenceUploader.module.css";

/** The raster types the server sniffs; a hint to the file picker, never a check. */
const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp";
/** The logo also takes an SVG (mockup v3 P5), cleaned by the server. */
const LOGO_TYPES = "image/svg+xml,image/png,image/jpeg,image/webp";

export interface ReferenceSlot {
  readonly key: AssetKey;
  /** The stored image of the latest version, or `null` when the key holds none. */
  readonly image: { readonly src: string; readonly alt: string } | null;
  /** The name of the file chosen for this slot, or `null`. */
  readonly fileName: string | null;
  readonly alt: string;
  readonly fileError?: string | undefined;
  readonly altError?: string | undefined;
  /** The server's refusal of this slot's upload. */
  readonly refusal?: string | undefined;
}

interface SlotActions {
  /** The slot whose upload is in flight: nothing else can be sent until it answers. */
  readonly pendingKey: AssetKey | null;
  readonly onFile: (key: AssetKey, file: File) => void;
  readonly onAltChange: (key: AssetKey, value: string) => void;
  readonly onUpload: (key: AssetKey) => void;
  /** A file dropped on the logo's zone: uploaded at once (mockup v3 P5). */
  readonly onDrop: (key: AssetKey, file: File) => void;
  /** The announcement of the last upload, when it belongs to this part. */
  readonly uploaded: string | null;
}

function fileInputId(key: AssetKey): string {
  return `reference-${key}-file`;
}

/** The native control stays (focus, keyboard, screen readers) but is not drawn: its text is the browser's. */
function FilePicker({
  slot,
  disabled,
  onFile,
}: {
  readonly slot: ReferenceSlot;
  readonly disabled: boolean;
  readonly onFile: (key: AssetKey, file: File) => void;
}): ReactElement {
  const id = fileInputId(slot.key);
  return (
    <input
      id={id}
      className={styles.fileInput}
      type="file"
      accept={slot.key === "logo" ? LOGO_TYPES : ACCEPTED_TYPES}
      disabled={disabled}
      aria-invalid={slot.fileError !== undefined}
      aria-describedby={describedBy(id, { error: slot.fileError })}
      onChange={(event) => {
        const input = event.target;
        const file = input.files?.[0];
        if (file !== undefined) onFile(slot.key, file);
        // Cleared so choosing the same file again still fires a change.
        input.value = "";
      }}
    />
  );
}

/** What a slot asks once a file is chosen: the file's name, its alt text, its refusals and its upload. */
function SlotDraft({
  slot,
  name,
  actions,
}: {
  readonly slot: ReferenceSlot;
  /** The slot's name ("Logo", "Imagen de referencia 2"), which names the group. */
  readonly name: string;
  readonly actions: SlotActions;
}): ReactElement | null {
  const { t } = useTranslation();
  if (slot.fileName === null) return null;
  const altId = `reference-${slot.key}-alt`;
  const busy = actions.pendingKey !== null;
  return (
    <div className={styles.draft} role="group" aria-label={name}>
      <p className={styles.fileName}>{slot.fileName}</p>
      {slot.fileError === undefined ? null : (
        <p id={errorId(fileInputId(slot.key))} className={styles.refusal} role="alert">
          {slot.fileError}
        </p>
      )}
      <Field id={altId} label={t("design.references.alt")} hint={t("design.references.altHint")} error={slot.altError} required>
        <Input
          id={altId}
          value={slot.alt}
          onChange={(value) => {
            actions.onAltChange(slot.key, value);
          }}
          invalid={slot.altError !== undefined}
          describedBy={describedBy(altId, { hint: t("design.references.altHint"), error: slot.altError })}
          disabled={busy}
          // Enter would submit the profile form around the uploader: it uploads this image instead (the field is disabled while one is in flight).
          onEnter={() => {
            actions.onUpload(slot.key);
          }}
        />
      </Field>
      {slot.refusal === undefined ? null : (
        <p role="alert" className={styles.refusal}>
          {slot.refusal}
        </p>
      )}
      <div>
        <Button
          variant="secondary"
          size="sm"
          loading={actions.pendingKey === slot.key}
          disabled={busy}
          onClick={() => {
            actions.onUpload(slot.key);
          }}
        >
          {t("design.references.upload")}
        </Button>
      </div>
    </div>
  );
}

/** The logo: one compact drop zone (the mockup's `.drop`), with a small thumbnail once stored. */
export function LogoDrop({ slot, ...actions }: { readonly slot: ReferenceSlot } & SlotActions): ReactElement {
  const { t } = useTranslation();
  // Whether a dragged file is over the zone (the mockup's `.drop.is-over`): what it looks like only.
  const [over, setOver] = useState(false);
  const busy = actions.pendingKey !== null;
  const hold = (event: DragEvent<HTMLDivElement>): void => {
    // Without this the browser opens the dropped file instead of handing it to the zone.
    event.preventDefault();
    if (!busy) setOver(true);
  };
  return (
    <div className={styles.logo} data-testid={`reference-${slot.key}`}>
      <div
        className={styles.drop}
        data-testid="logo-drop"
        data-over={over ? "true" : undefined}
        onDragEnter={hold}
        onDragOver={hold}
        onDragLeave={(event) => {
          // Leaving for a child of the zone is not leaving the zone.
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          const file = event.dataTransfer.files[0];
          if (file !== undefined && !busy) actions.onDrop(slot.key, file);
        }}
      >
        {slot.image === null ? (
          <Icon name="upload" size="md" />
        ) : (
          <img className={styles.logoImage} src={slot.image.src} alt={slot.image.alt} />
        )}
        <FilePicker slot={slot} disabled={busy} onFile={actions.onFile} />
        <label className={styles.pick} htmlFor={fileInputId(slot.key)}>
          {t("design.logo.drop")}
        </label>
        <span className={styles.help}>{t("design.logo.types")}</span>
      </div>
      <SlotDraft slot={slot} name={t("design.editor.logo")} actions={actions} />
      {actions.uploaded === null ? null : (
        <p role="status" className={styles.status}>
          {actions.uploaded}
        </p>
      )}
    </div>
  );
}

/** The four reference images as small thumbnail tiles; a tile is its own file picker. */
export function ReferenceGrid({ slots, ...actions }: { readonly slots: readonly ReferenceSlot[] } & SlotActions): ReactElement {
  const { t } = useTranslation();
  const referenceName = (key: AssetKey): string =>
    t("design.references.slot.image", { number: key.slice("image-".length) });
  return (
    <div className={styles.references}>
      <ul className={styles.tiles}>
        {slots.map((slot) => (
          <li key={slot.key} className={styles.slot} data-testid={`reference-${slot.key}`}>
            <div className={styles.tile} data-filled={slot.image === null ? undefined : "true"}>
              {slot.image === null ? (
                <Icon name="plus" size="sm" />
              ) : (
                <img className={styles.thumb} src={slot.image.src} alt={slot.image.alt} />
              )}
              <FilePicker slot={slot} disabled={actions.pendingKey !== null} onFile={actions.onFile} />
              <label className={styles.tileLabel} htmlFor={fileInputId(slot.key)}>
                <span className={styles.srOnly}>{referenceName(slot.key)}</span>
              </label>
            </div>
            <div className={styles.slotDraft}>
              <SlotDraft slot={slot} name={referenceName(slot.key)} actions={actions} />
            </div>
          </li>
        ))}
      </ul>
      <p className={styles.help}>{t("design.references.hint")}</p>
      {actions.uploaded === null ? null : (
        <p role="status" className={styles.status}>
          {actions.uploaded}
        </p>
      )}
    </div>
  );
}
